import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Divider, Row, Col, Space, Typography, Card } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import { selectCurrentPath, selectStepperState } from '../../../store/slices/path.slice';
import { transformFormFields, transformInvestmentStyle } from './pathSummaryTransforms';
import { allocatePortfolios } from '../../../modules/Inference/inferenceApi';
import { selectAllocationSummary, setMergedPortfolios } from "../../../store/slices/path.slice";
import { fetchDetailedPortfolioObjects } from "../../../api/portfolioApi";
import { selectFullPortfolios } from "../../../store/slices/portfolios.slice";
import { usePaths } from "../usePaths";
import usePathHelpers from "../usePathHelpers";

const { Title, Text } = Typography;

const PathSummary = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [hasFetched, setHasFetched] = useState(false);

  // Get both path and form data from Redux
  const { pathId, portfolioIds, isEditing } = useSelector(selectCurrentPath);
  const { formData } = useSelector(selectStepperState);
  const { loading, error, data: allocationData } = useSelector(selectAllocationSummary);

  // Memoize the allocated portfolio IDs to prevent unnecessary recalculations
  const allocatedPortfolioIds = useMemo(() =>
    allocationData?.map?.(portfolio => portfolio.id) || [],
    [allocationData]
  );

  const fullPortfolios = useSelector(state => selectFullPortfolios(state, allocatedPortfolioIds));

  // Effect to fetch portfolio details only when needed
  useEffect(() => {
    const missingPortfolios = allocatedPortfolioIds.filter(
      id => !fullPortfolios.find(p => p.id === id)
    );

    if (missingPortfolios.length > 0) {
      dispatch(fetchDetailedPortfolioObjects(missingPortfolios));
    }
    setHasFetched(true);
  }, [allocatedPortfolioIds, dispatch]);

  // Reset hasFetched when allocation data changes
  useEffect(() => {
    setHasFetched(false);
  }, [allocationData]);

  const mergedPortfolios = useMemo(() => {
    if (!allocationData || !fullPortfolios.length) return [];

    return allocationData.map(allocatedPortfolio => {
      const fullPortfolio = fullPortfolios.find(p => p.id === allocatedPortfolio.id);
      return {
        ...fullPortfolio,
        allocation: allocatedPortfolio.allocation,
        weight: allocatedPortfolio.wgt,
        reasoning: allocatedPortfolio.reasoning
      };
    }).filter(Boolean);
  }, [allocationData, fullPortfolios]);

  const updateMergedPortfolios = useCallback(() => {
    if (mergedPortfolios.length > 0) {
      dispatch(setMergedPortfolios([...mergedPortfolios]));
    }
  }, [mergedPortfolios, dispatch]);

  // Use the timeseries hook
  const { hasFetchedBlended, timeseriesError } = usePaths(mergedPortfolios);

  useEffect(() => {
    updateMergedPortfolios();
  }, [updateMergedPortfolios]);


  const handleSubmit = async () => {
    try {
      const transformedData = transformFormFields(formData);
      const allocationResult = await dispatch(allocatePortfolios({
        portfolioIds: portfolioIds,
        responses: {
          ...transformedData,
        }
      })).unwrap();

      navigate(`/portfolios/paths/results`);
    } catch (error) {
      console.error('Error in submission process:', error);
    }
  };

  const handleSaveAndSubmit = () => {
    handleSubmit();
  };

  return (
    <Card className="bg-white rounded-lg shadow-lg my-6" style={{maxWidth: 900, margin: "auto"}}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Title level={3} className="mb-4">Selected Path Details</Title>

        <div>
          <Title level={4} className="mb-4">Path Configuration</Title>
          {/*{Object.entries(formData).map(([fieldId, value]) =>*/}
          {/*  renderFormFieldSummary(fieldId, value)*/}
          {/*)}*/}
        </div>

        <ButtonGroup style={{width: 400}}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveAndSubmit}
            fullWidth
          >
            Save and Submit
          </Button>
        </ButtonGroup>
      </Space>
    </Card>
  );
};

export default PathSummary;